import { PreviewTypes, WidgetLocations, WidgetPositions, WidgetTypes } from '@enums';
import { BaseWidgetConfig, WidgetAutoPlacement } from '@interfaces';
import {
  FREE_THEME_TARGETS,
  PAID_THEME_TARGETS,
  ThemeTarget,
  WidgetConfigs
} from '@utils/constants';
import { DebugLogger } from '@inspiredmember/debug-logger';
import { CreateElement } from '@utils/helper-functions';

const logger = DebugLogger.getInstance();

export const initializeWidgets = (data: any) => {
  startPolling(data);
  const theme = (window as any).Shopify?.theme;
  let themeId = theme?.themeStoreId || theme?.theme_store_id;
  if (!themeId && theme?.name === 'test-data') {
    themeId = 'test-data';
  }
  const onFreeTheme = !!FREE_THEME_TARGETS[themeId];

  WidgetConfigs.forEach((widgetConfig: any) => {
    const widgetData: BaseWidgetConfig = data.customization[widgetConfig.name];
    const storedPreviewData = sessionStorage.getItem('__inspired_preview__');
    const previewData = storedPreviewData ? JSON.parse(storedPreviewData) : {};

    if (widgetData) {
      //check for cart drawer preview
      if (widgetConfig.tagName === WidgetTypes.EmbeddedWidget || widgetConfig.tagName === WidgetTypes.CartProgressWidget) {
        if (storedPreviewData) {

          if (previewData?.previewType === PreviewTypes.EmbeddedCartDrawer || previewData?.previewType === PreviewTypes.ProgressCartDrawer) {
            const cartDrawerTarget = getAutoTargetSelector(
              onFreeTheme,
              themeId,
              WidgetLocations.CartDrawer
            );
            if (cartDrawerTarget) {
              const cartDrawerPlacement = widgetData?.placements || {} as WidgetAutoPlacement;

              const previewPlacement = {
                active: true,
                customTarget: cartDrawerPlacement.customTarget ?? cartDrawerTarget,
                location: WidgetLocations.CartDrawer,
                position: cartDrawerPlacement.position ?? WidgetPositions.Before
              };

              widgetData.placements = previewPlacement;
            }
          }
        }
      }
      if (
        widgetConfig.tagName === WidgetTypes.FloatingWidget &&
        !document.querySelector('inspired-floating-widget')
      ) {
        // for floating widget, append directly to the body
        if (!widgetData?.placements?.active && previewData?.previewType !== PreviewTypes.Floating) return;

        const floatingElement = createEl(widgetConfig.tagName);
        floatingElement.setAttribute('floating', '');
        document.body.appendChild(floatingElement);
        // instant preview related code
        const shadowRoot = document.querySelector('inspired-static-floating')?.shadowRoot;
        const widget = shadowRoot?.querySelector('#inspired-floating-static');
        if (widget) {
          widget.remove();
        }
      } else if (widgetConfig.tagName === WidgetTypes.BannerWidget &&
        !document.querySelector('inspired-announcement-banner')) {
        if (!widgetData?.placements?.active && previewData?.previewType !== PreviewTypes.Banner) return;

        const bannerElement = createEl(widgetConfig.tagName);
        bannerElement.setAttribute('banner', '');

        if (widgetData?.placements?.customTarget) {
          appendWidget(widgetData?.placements?.customTarget, bannerElement, widgetData?.placements?.position);
          // instant preview related code
          const shadowRoot = document.querySelector('inspired-static-announcement-banner')?.shadowRoot;
          const widget = shadowRoot?.querySelector('#inspired-banner-static');
          if (widget) {
            widget.remove();
          }

          return;
        }

        document.body.prepend(bannerElement);
        // instant preview related code
        const shadowRoot = document.querySelector('inspired-static-announcement-banner')?.shadowRoot;
        const widget = shadowRoot?.querySelector('#inspired-banner-static');
        if (widget) {
          widget.remove();
        }
      } else if (widgetConfig.tagName === WidgetTypes.EmbeddedWidget || widgetConfig.tagName === WidgetTypes.CartProgressWidget) {
        // custom target placement
        const storedPreviewData = sessionStorage.getItem('__inspired_preview__');
        let previewData;

        if (storedPreviewData) {
          previewData = JSON.parse(storedPreviewData);
        }

        if (!widgetData?.placements) return;

        if (widgetConfig.name != previewData?.previewType && storedPreviewData) return;

        const placement = widgetData?.placements || {};

        if (!placement?.customTarget) {
          // no custom target, get target from theme dictionary if it exists
          placement.customTarget = getAutoTargetSelector(
            onFreeTheme,
            themeId,
            placement?.location as keyof ThemeTarget
          );
        }

        if ((storedPreviewData || placement.active) && placement.customTarget) {
          const embeddedElement = createEl(widgetConfig.tagName);
          embeddedElement.setAttribute('auto-embedded', '');
          if (placement.location === WidgetLocations.CartDrawer)
            embeddedElement.setAttribute(WidgetLocations.CartDrawer, '');

          embeddedElement.style.padding = '1rem 0';
          embeddedElement.style.display = 'block';
          appendWidget(placement.customTarget, embeddedElement, placement.position);
        }
      }
      // app blocks will initialize themselves
    }
  });
};

export const insertWidgetAtPosition = (widget: HTMLElement, target: Element, position: string) => {
  const parentNode = target.parentNode;
  if (parentNode && !parentNode.querySelector('[auto-embedded]')) {
    switch (position) {
      case WidgetPositions.Before:
        target.parentNode?.insertBefore(widget, target);
        break;
      case WidgetPositions.After:
        target.parentNode?.insertBefore(widget, target.nextSibling);
        break;
      case WidgetPositions.InsideTop:
        target.insertBefore(widget, target.firstChild);
        break;
      case WidgetPositions.InsideBottom:
        target.appendChild(widget);
        break;
    }
    // re-append widgets that are removed from DOM
    observeWidget(widget, target, position);
  }
};

export const appendWidget = (target: string, widget: HTMLElement, position = 'before') => {
  const targetElement = document.querySelector(target);
  if (targetElement) {
    insertWidgetAtPosition(widget, targetElement, position);
  }
};

function observeWidget(widget: HTMLElement, target: Element, position: string) {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (!document.body.contains(widget)) {
        logger.debug('Widget removed from DOM, re-appending all widgets that were removed');
        // disconnect this observer as a new one will be created when it's re-appended
        observer.disconnect();
        insertWidgetAtPosition(widget, target, position);
      }
    });
  });

  const config = { childList: true, subtree: true, attributes: true };
  if (widget.parentNode) {
    observer.observe(widget.parentNode, config);
  } else {
    logger.error(
      "Failed to observe widget, it doesn't have a parent node and is not part of the DOM"
    );
  }
}

function startPolling(data: any) {
  if ((window as any)._pollingStarted) return;

  (window as any)._pollingStarted = true;
  const POLLING_INTERVAL = 2000;

  setInterval(() => {
    try {
      initializeWidgets(data);
    } catch (error) {
      logger.error('Error during polling:', error);
    }
  }, POLLING_INTERVAL);
}

function createEl(tag: string) {
  const embeddedEl = CreateElement(tag);
  embeddedEl.style.visibility = 'inherit';
  embeddedEl.setAttribute('auto-embedded', '');
  return embeddedEl;
}

function getAutoTargetSelector(onFreeTheme: boolean, themeId: number, type: keyof ThemeTarget) {
  return onFreeTheme ? FREE_THEME_TARGETS[themeId]?.[type] : PAID_THEME_TARGETS[themeId]?.[type];
}
